import { getEventFlyerUrl } from "./eventMedia";

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "") ?? "";
}

function firstText(...values) {
  const value = firstValue(...values);
  return typeof value === "string" ? value.trim() : value;
}

function isFreeValue(value) {
  if (value === true || value === 0) return true;
  return ["free", "£0", "£0.00", "0", "0.00"].includes(
    String(value || "").trim().toLowerCase()
  );
}

export function normalizeEventRecord(idOrEvent, data = {}) {
  const source =
    typeof idOrEvent === "object" && idOrEvent !== null
      ? idOrEvent
      : { ...data, id: idOrEvent || data.id };
  const cost = firstText(source.cost, source.price, source.fee);

  return {
    ...source,
    id: source.id || "",
    type: "event",
    title: firstText(source.title, source.name) || "Untitled event",
    description: firstText(source.description, source.summary),
    startDate: firstValue(source.startDate, source.date),
    endDate: firstValue(source.endDate),
    startTime: firstText(source.startTime, source.time),
    endTime: firstText(source.endTime),
    category: firstText(source.category, source.eventCategory),
    venueName: firstText(source.venueName, source.location),
    address: firstText(source.address, source.venueAddress),
    postcode: firstText(source.postcode, source.venuePostcode),
    organiserName: firstText(source.organiserName, source.organisationName),
    contact: firstText(source.contact, source.contactInfo),
    website: firstText(source.website),
    bookingUrl: firstText(source.bookingUrl, source.registrationUrl),
    cost,
    isFree: source.isFree === true || isFreeValue(cost),
    flyerUrl: getEventFlyerUrl(source),
  };
}

function dateFromValue(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDate) {
      return new Date(
        Number(isoDate[1]),
        Number(isoDate[2]) - 1,
        Number(isoDate[3])
      );
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formattedDate(value) {
  const date = dateFromValue(value);
  if (!date) return typeof value === "string" ? value.trim() : "";

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatEventDateLabel(event = {}) {
  const normalized = normalizeEventRecord(event);
  const start = formattedDate(normalized.startDate || normalized.eventDate);
  const end = formattedDate(normalized.endDate);

  if (!start) return "Date to be confirmed";
  if (end && end !== start) return `${start} – ${end}`;
  return start;
}

export function formatEventTimeLabel(event = {}) {
  const normalized = normalizeEventRecord(event);
  if (!normalized.startTime) return "";
  return normalized.endTime
    ? `${normalized.startTime} – ${normalized.endTime}`
    : normalized.startTime;
}

export function getEventCostLabel(event = {}) {
  const normalized = normalizeEventRecord(event);
  if (normalized.isFree) return "Free";
  return normalized.cost ? String(normalized.cost) : "";
}
